import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Trophy, Award, TrendingUp, ArrowUp, ArrowDown, Minus, User as UserIcon, Clock, Shield } from 'lucide-react';
import { useAuthStore } from '@fog-chess/core';
import { getReplayHistory } from '@fog-chess/core';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { RatingGraph } from '@/components/RatingGraph';
import { calculateRatingTrajectory } from '@/lib/ratingGraph';

/**
 * ProfileScreen - TanStack Query driven (server state boundary respected).
 *
 * - Fetches replay history via getReplayHistory({ limit: 50 })
 * - Computes wins/losses/draws from replay entries
 * - Shows current FTPES tier
 * - Renders client-side RatingGraph calculated from my_delta values
 */

interface ReplayEntry {
  match_id: string;
  endgame_type: string;
  winner_id: string | null;
  played_at: string;
  my_color: 'white' | 'black';
  my_delta: number;
  my_score: number;
  opponent_id: string;
}

interface ProfileStats {
  wins: number;
  losses: number;
  draws: number;
  total: number;
  currentScore: number;
  currentStage: number;
}

const DRAW_TYPES = [
  'stalemate',
  'insufficient_material',
  'threefold_repetition',
  'fifty_move_rule',
  'fivefold_repetition',
  'seventy_five_move_rule',
  'draw_agreement',
  'draw_claim',
] as const;

function isDraw(type: string): boolean {
  return (DRAW_TYPES as readonly string[]).includes(type);
}

function calculateStats(entries: ReplayEntry[]): ProfileStats {
  let wins = 0;
  let losses = 0;
  let draws = 0;

  for (const entry of entries) {
    if (isDraw(entry.endgame_type)) {
      draws++;
    } else if (entry.winner_id === entry.opponent_id) {
      losses++;
    } else {
      wins++;
    }
  }

  // Use the most recent entry's my_score as current, or 0
  const currentScore = entries[0]?.my_score ?? 0;

  // Stage calculation mirrors backend
  let currentStage = 1;
  if (currentScore >= 2000) currentStage = 5;
  else if (currentScore >= 1500) currentStage = 4;
  else if (currentScore >= 1000) currentStage = 3;
  else if (currentScore >= 500) currentStage = 2;

  return {
    wins,
    losses,
    draws,
    total: entries.length,
    currentScore,
    currentStage,
  };
}

function useReplayHistory() {
  return useQuery({
    queryKey: ['replay-history', 50],
    queryFn: () => getReplayHistory({ limit: 50 }),
    staleTime: 30_000,
    refetchInterval: 60_000,
    retry: 1,
  });
}

function useMyRank() {
  return useQuery({
    queryKey: ['my-rank'],
    queryFn: () =>
      fetch('/api/v1/leaderboard/me')
        .then((r) => {
          if (!r.ok) throw new Error('Failed to fetch rank');
          return r.json();
        })
        .then((data) => data as { rank: number; score: number; stage: number }),
    staleTime: 30_000,
    refetchInterval: 60_000,
    retry: 1,
  });
}

function StatCard({
  label,
  value,
  icon,
  color,
  trend,
}: {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  trend?: { value: number; label: string };
}) {
  return (
    <Card className="flex-1">
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{label}</p>
            <p className="text-2xl font-bold text-foreground mt-1">{value}</p>
            {trend && (
              <p className={cn(
                'text-xs mt-1 flex items-center gap-1',
                trend.value >= 0 ? 'text-emerald-600' : 'text-rose-600',
              )}>
                {trend.value >= 0 ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
                {trend.label}
              </p>
            )}
          </div>
          <div className={cn('flex h-12 w-12 items-center justify-center rounded-xl', color)}>
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function RecentMatches({ entries }: { entries: ReplayEntry[] }) {
  if (entries.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Shield className="h-12 w-12 mx-auto mb-3 opacity-30" />
        <p>No matches played yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-3 max-h-96 overflow-y-auto">
      {entries.slice(0, 10).map((entry) => {
        const isDraw = DRAW_TYPES.includes(entry.endgame_type as any);
        const won = !isDraw && entry.winner_id !== entry.opponent_id;
        const lost = !isDraw && entry.winner_id === entry.opponent_id;

        return (
          <motion.div
            key={entry.match_id}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.2, delay: 0.02 }}
            className={cn(
              'flex items-center justify-between p-3 rounded-lg border transition-colors',
              isDraw && 'bg-amber-50/50 dark:bg-amber-900/20',
              won && 'bg-emerald-50/50 dark:bg-emerald-900/20',
              lost && 'bg-rose-50/50 dark:bg-rose-900/20',
            )}
          >
            <div className="flex items-center gap-3">
              <div className={cn(
                'flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold',
                isDraw && 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300',
                won && 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300',
                lost && 'bg-rose-100 text-rose-700 dark:bg-rose-900 dark:text-rose-300',
              )}>
                {isDraw ? '½' : won ? '1' : '0'}
              </div>
              <div>
                <p className="text-sm font-medium">
                  {entry.endgame_type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                </p>
                <p className="text-xs text-muted-foreground font-mono">
                  {new Date(entry.played_at).toLocaleDateString()}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-right">
              <span className={cn(
                'text-sm font-medium font-mono',
                won && 'text-emerald-600',
                lost && 'text-rose-600',
                isDraw && 'text-amber-600',
              )}>
                {won ? '+' : lost ? '' : '±'}{entry.my_delta}
              </span>
              <Badge variant="outline" className="text-xs">
                {entry.my_score} FTPES
              </Badge>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}

function ProfileSkeleton() {
  return (
    <div className="space-y-6">
      <div className="flex gap-4">
        <Skeleton className="h-24 w-24 rounded-full" />
        <div className="flex-1 space-y-3">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-32" />
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-28 w-full" />
        ))}
      </div>
      <Skeleton className="h-96 w-full rounded-lg border" />
    </div>
  );
}

export function ProfileScreen() {
  const user = useAuthStore((s) => s.user);
  const { data: replayData, isLoading: loadingHistory } = useReplayHistory();
  const { data: myRankData, isLoading: loadingRank } = useMyRank();

  const entries = replayData?.items ?? [];
  const stats = calculateStats(entries);
  const trajectory = calculateRatingTrajectory(entries);

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
      >
        <div className="flex items-center gap-4">
          <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-primary/15 text-primary">
            <UserIcon className="h-10 w-10" />
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{user?.username ?? 'Player'}</h1>
            <p className="text-muted-foreground">Fog Chess Profile</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="default" className="gap-1.5">
            <Award className="h-3.5 w-3.5" />
            <span>{stats.currentScore} FTPES</span>
          </Badge>
          <Badge variant="fog" className="gap-1.5">
            <Trophy className="h-3.5 w-3.5" />
            Stage {stats.currentStage}
          </Badge>
          {myRankData && (
            <Badge variant="outline" className="gap-1">
              <TrendingUp className="h-3 w-3" />
              Rank #{myRankData.rank}
            </Badge>
          )}
        </div>
      </motion.div>

      {/* Stats Grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Total Matches"
          value={stats.total}
          icon={<Clock className="h-6 w-6" />}
          color="bg-sky-500/15 text-sky-600 dark:text-sky-400"
        />
        <StatCard
          label="Wins"
          value={stats.wins}
          icon={<ArrowUp className="h-6 w-6" />}
          color="bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
        />
        <StatCard
          label="Losses"
          value={stats.losses}
          icon={<ArrowDown className="h-6 w-6" />}
          color="bg-rose-500/15 text-rose-600 dark:text-rose-400"
        />
        <StatCard
          label="Draws"
          value={stats.draws}
          icon={<Minus className="h-6 w-6" />}
          color="bg-amber-500/15 text-amber-600 dark:text-amber-400"
        />
      </div>

      {/* Rating Graph + Recent Matches */}
      <div className="grid gap-6 lg:grid-cols-3">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="lg:col-span-2"
        >
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-primary" />
                Rating Trajectory
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(loadingHistory || loadingRank) ? (
                <ProfileSkeleton />
              ) : trajectory.length < 2 ? (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <TrendingUp className="h-12 w-12 mb-3 opacity-30" />
                  <p>Play matches to see your rating trajectory</p>
                  <p className="text-sm">Your FTPES history will appear here after your first game</p>
                </div>
              ) : (
                <RatingGraph data={trajectory} height={320} />
              )}
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="h-4 w-4" />
                Recent Matches
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(loadingHistory || loadingRank) ? (
                <div className="space-y-3">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <Skeleton key={i} className="h-14 w-full" />
                  ))}
                </div>
              ) : (
                <RecentMatches entries={entries} />
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}